import {
  amountInWords,
  buildPurchaseOrder,
  typedRatePaisa,
  canMovePurchaseOrder,
  purchaseLineCost,
  storedLineAmount,
  unitWords,
  purchaseProgress,
  purchaseRemaining,
  setPurchaseOrderStatus,
  type PurchaseOrderStatus,
  type SyncOp,
} from '@ims/core'
import type {
  GoodsReceiptSummary,
  Page,
  ProductRow,
  PurchaseOrderBoardRow,
  PurchaseOrderRow,
  RowOf,
  SupplierBillSummary,
  ProductCodeRow,
} from '@ims/data'
import {
  arrivalClass,
  Badge,
  Button,
  buttonClass,
  Card,
  ConfirmDialog,
  cx,
  EmptyState,
  Field,
  FilterPill,
  FilterPills,
  IconButton,
  IconTile,
  Kbd,
  ListFrame,
  MenuItem,
  Modal,
  ListToolbar,
  PageHeader,
  Pagination,
  RowActions,
  RowNote,
  RowTitle,
  SearchInput,
  Segmented,
  Select,
  SkeletonDetail,
  SkeletonTable,
  SplitButton,
  Stat,
  StateSwap,
  Table,
  TD,
  Textarea,
  TH,
  TR,
  useArrivals,
  useToast,
} from '@ims/ui'
import {
  ArrowLeft,
  Ban,
  Check,
  ClipboardList,
  Clock,
  Copy,
  FilePdf,
  FilePen,
  PackageCheck,
  PackagePlus,
  Pencil,
  Printer,
  Send,
  Share2,
  Trash2,
} from '@ims/ui/icons'
import { useEffect, useEffectEvent, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import {
  DateInput,
  LocationSelect,
  MoneyInput,
  ProductPicker,
  QtyInput,
  useLookups,
} from '../components.tsx'
import { errorMessage, useBusinessName, useLive, useSettings } from '../hooks.ts'
import {
  dateLabel,
  dateShort,
  dateTime,
  money,
  printDateLabel,
  qty,
  todayIso,
} from '../lib/format.ts'
import { percentText } from '../lib/discount-mode.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { numberingFor } from '../lib/numbering.ts'
import { SupplierPicker } from '../party-picker.tsx'
import { finePointer } from '../lib/pointer.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { useStaffNames } from '../lib/staff.ts'
import {
  dueWords,
  purchaseBoardState,
  purchaseOrderRow,
  purchaseOrderState,
  purchaseOrderStanding,
  purchaseOrderText,
  readGenerateChoice,
  saveGenerateChoice,
  type GenerateChoice,
} from '../lib/purchases.ts'
import { purchaseDocView } from '../lib/doc-view.ts'
import { printName } from '../print.tsx'
import { BandStat, Meter, PartyCard } from './doc-band.tsx'
import { DocPrintDialog, type DocToPrint } from './print-dialog.tsx'
import { DocPrintMenu, openPrintMenu } from './print-menu.tsx'
import { countdown } from '../lib/sales-orders.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { needsApproval } from '../lib/approvals.ts'
import { ApprovalCard, poDoc, useApprovalContext } from './approvals.tsx'
import { CANCELLED_ROW } from './invoices.tsx'
import { WhatToOrder } from './reorder.tsx'
import { PartyForm } from './parties.tsx'
import { SupplierStatementSheet, SupplierStatementTable } from './supplier-statement-sheet.tsx'
import type { SupplierLedger } from '../lib/supplier-statement.ts'
import { clearDraft, draftKey, readDraft, saveDraft } from '../lib/drafts.ts'
import { parsePoDraft, poDraftHasWork, type PoDraft } from '../lib/po-draft.ts'
import { DraftBanner, DroppedNote } from './draft-banner.tsx'
import { ReceiveStockDialog, type DueLine } from './receive.tsx'
import { billStatus } from '../lib/payables.ts'
import {
  PAYABLE_TABLES,
  PaySupplierDialog,
  ReceiptBillCard,
  SupplierMoreButtons,
  SupplierPayButton,
  SupplierMoney,
  useSupplierDialogs,
  useReceiptBills,
} from './supplier-bills.tsx'
import { CorrectRateDialog } from './receipt-correct.tsx'
import { SectionTabs } from '../layout/sections.tsx'

const PAGE = 25

type SupplierRow = RowOf<'suppliers'>

/** Active suppliers by name, and a lookup that also finds inactive ones already on a document. */
function useSuppliers(): { list: SupplierRow[]; name: (id: string | null) => string | null } {
  const rows =
    useLive(
      (r) => r.list('suppliers', { orderBy: { column: 'name' }, limit: 2000 }),
      [],
      ['suppliers'],
    ).data?.rows ?? []
  return {
    list: rows.filter((s) => s.is_active),
    name: (id) => rows.find((s) => s.id === id)?.name ?? null,
  }
}

// -----------------------------------------------------------------------------
// Purchase orders
// -----------------------------------------------------------------------------

type OrderShow = 'open' | 'finished' | 'reorder'
type OrderPill = 'draft' | 'waiting' | 'ready' | 'sent' | 'part'
const ORDER_PILLS: { value: OrderPill; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'ready', label: 'Ready to send' },
  { value: 'waiting', label: 'Waiting approval' },
  { value: 'sent', label: 'Sent' },
  { value: 'part', label: 'Part received' },
]
const ORDER_TABLES = [
  'purchase_orders',
  'purchase_order_lines',
  'goods_receipts',
  'goods_receipt_lines',
  'suppliers',
] as const

/**
 * A page of orders, each with what has arrived of it. Online-only (no local database)
 * there is no open/finished split: every order, newest first.
 */
function useOrders(filter: {
  show?: Exclude<OrderShow, 'reorder'>
  state?: OrderPill
  supplierId?: string
  search?: string
  limit: number
  offset?: number
}) {
  const today = todayIso()
  return useLive(
    async (repo): Promise<Page<PurchaseOrderBoardRow>> => {
      if (repo.purchases.orderBoard) return repo.purchases.orderBoard({ ...filter, today })
      const page = await repo.purchases.orders({
        supplierId: filter.supplierId,
        search: filter.search,
        limit: filter.limit,
        offset: filter.offset,
      })
      const details = await Promise.all(page.rows.map((o) => repo.purchases.orderDetail(o.id)))
      return {
        total: page.total,
        rows: page.rows.map((order, i) => {
          const d = details[i]
          const done = d ? purchaseProgress(d.lines, d.receivedByLine) : null
          return {
            order,
            supplierName: null,
            lineCount: d?.lines.length ?? 0,
            orderedMilli: done?.orderedMilli ?? 0,
            receivedMilli: Math.min(done?.receivedMilli ?? 0, done?.orderedMilli ?? 0),
            duePaisa: 0,
          }
        }),
      }
    },
    [
      filter.show,
      filter.state,
      filter.supplierId,
      filter.search,
      filter.limit,
      filter.offset,
      today,
    ],
    [...ORDER_TABLES],
  )
}

type Lookups = ReturnType<typeof useLookups>

/** An order with what its buttons need: the lines, their products and the supplier. */
async function loadOrder(repo: ReturnType<typeof useRepo>, id: string) {
  const d = await repo.purchases.orderDetail(id)
  if (!d) return null
  const [products, supplier] = await Promise.all([
    Promise.all(d.lines.map((l) => repo.get('products', l.product_id))),
    d.order.supplier_id ? repo.get('suppliers', d.order.supplier_id) : null,
  ])
  return { ...d, products, supplier }
}
type LoadedOrder = NonNullable<Awaited<ReturnType<typeof loadOrder>>>

/** A purchase order ready for Print or save. */
function purchasePrint(
  {
    order,
    lines,
    products,
    supplier,
  }: Pick<LoadedOrder, 'order' | 'lines' | 'products' | 'supplier'>,
  lookups: Lookups,
  settings: ReturnType<typeof useSettings>,
): DocToPrint {
  return {
    doc: purchaseDocView({
      order,
      supplier,
      // Only worth saying when the business has more than one place.
      deliverTo:
        lookups.activeLocations.length > 1
          ? (lookups.location(order.location_id)?.name ?? null)
          : null,
      settings,
      lines: lines.map((l, i) => ({
        id: l.id,
        line_no: l.line_no,
        name: products[i]?.name ?? 'Product',
        sku: products[i]?.sku ?? null,
        unit: lookups.unit(products[i]?.unit_id)?.symbol ?? '',
        qty_ordered_milli: l.qty_ordered_milli,
        unit_cost_paisa: l.unit_cost_paisa,
      })),
    }),
    name: printName('Purchase-order', order),
    summary: [
      order.number ?? 'Purchase order (number on sync)',
      supplier?.name,
      money(order.total_paisa, settings),
    ]
      .filter(Boolean)
      .join(' · '),
  }
}

/** What is still to arrive on an order, as the receive dialog takes it. */
function dueLines({ lines, products, receivedByLine }: LoadedOrder): DueLine[] {
  return lines.flatMap((l, i) => {
    const product = products[i]
    const left = purchaseRemaining(l, receivedByLine)
    return product && left > 0
      ? [{ lineId: l.id, product, qtyMilli: left, costPaisa: l.unit_cost_paisa }]
      : []
  })
}

/** Copies text; false when the browser refuses (no permission, insecure page). */
async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** Whether an order is ready to go to its supplier: generated, or approved. */
export function readyToSend(order: Pick<PurchaseOrderRow, 'status'>): boolean {
  return order.status === 'issued' || order.status === 'approved'
}

/**
 * Sending an order that is ready (docs/plans/po-ready.md): print it, save it as a PDF or share
 * it as text, then mark it sent. The app cannot tell whether it reached the supplier, so
 * "Mark it sent" is the person's word, ticked for an order placed by phone or in person.
 */
function SendOrderDialog({ order: loaded, onClose }: { order: LoadedOrder; onClose: () => void }) {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const business = useBusinessName(current?.tenant.name)
  const [mark, setMark] = useState(true)
  const [busy, setBusy] = useState(false)
  const { order, lines, products, supplier } = loaded
  const name = order.number ?? 'this order'
  const canShare = typeof navigator.share === 'function'
  const text = purchaseOrderText(
    {
      business,
      number: order.number,
      supplier: supplier?.name ?? null,
      neededBy: order.expected_date ? printDateLabel(order.expected_date, settings) : null,
      notes: order.notes,
      totalPaisa: order.total_paisa,
      lines: lines.map((l, i) => ({
        name: products[i]?.name ?? 'Product',
        qtyMilli: l.qty_ordered_milli,
        unit: lookups.unit(products[i]?.unit_id)?.symbol ?? '',
        costPaisa: l.unit_cost_paisa,
      })),
    },
    (p) => money(p, settings),
    qty,
  )
  const [printing, setPrinting] = useState<DocToPrint | null>(null)
  const print = () => setPrinting(purchasePrint(loaded, lookups, settings))
  const share = async () => {
    if (!canShare) {
      if (await copyText(text)) toast.success('Copied. Paste it into WhatsApp, Viber or an email.')
      else toast.error('Couldn’t copy. Print the order or save it as a PDF instead.')
      return
    }
    try {
      await navigator.share({ title: `Purchase order ${order.number ?? ''}`.trim(), text })
    } catch (error) {
      // Closing the share sheet is not an error.
      if (!(error instanceof DOMException && error.name === 'AbortError'))
        toast.error(errorMessage(error))
    }
  }
  const done = async () => {
    if (!mark || !current) return onClose()
    setBusy(true)
    try {
      // As it is now: another device may have sent or cancelled it meanwhile.
      const now = await repo.get('purchase_orders', order.id)
      if (now && readyToSend(now)) {
        await repo.commit(
          setPurchaseOrderStatus(
            { tenantId: current.tenant_id, now: new Date().toISOString() },
            purchaseOrderRow(now),
            'sent',
          ).ops,
        )
        toast.success(`${name} marked as sent`)
      }
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  const option = (icon: ReactNode, label: string, sub: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface p-3 text-left transition-colors hover:bg-soft"
    >
      <IconTile area="buy">{icon}</IconTile>
      <span className="min-w-0">
        <span className="block font-semibold">{label}</span>
        <span className="block text-xs text-muted">{sub}</span>
      </span>
    </button>
  )
  // The send dialog steps aside while the order is printed or saved, and comes back after.
  if (printing) return <DocPrintDialog {...printing} onClose={() => setPrinting(null)} />
  return (
    <Modal
      open
      onClose={onClose}
      title={`Send ${name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void done()}>
            Done
          </Button>
        </>
      }
    >
      <p className="-mt-2 mb-3 text-sm text-muted">
        {supplier ? `To ${supplier.name} · ` : ''}
        {money(order.total_paisa, settings)}
      </p>
      <div className="space-y-2">
        {option(
          <Printer className="size-4" />,
          'Print or save',
          'A4 with your logo: print it, download the PDF or share the file',
          print,
        )}
        {canShare
          ? option(
              <Share2 className="size-4" />,
              'Share',
              'WhatsApp, Viber or email, as text',
              () => void share(),
            )
          : option(
              <Copy className="size-4" />,
              'Copy as text',
              'Then paste it into WhatsApp, Viber or an email',
              () => void share(),
            )}
      </div>
      <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-xl bg-soft p-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-4 accent-primary"
          checked={mark}
          onChange={(e) => setMark(e.target.checked)}
        />
        <span>
          <span className="block font-semibold">Mark it sent</span>
          <span className="block text-xs text-muted">
            Ordered by phone or in person? Leave this ticked and press Done.
          </span>
        </span>
      </label>
    </Modal>
  )
}

/** The buttons on a row of the list: receive, send and print, without opening the order. */
function useOrderActions() {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const [busy, setBusy] = useState<string | null>(null)
  const [receiving, setReceiving] = useState<{ order: PurchaseOrderRow; due: DueLine[] } | null>(
    null,
  )
  const [sending, setSending] = useState<LoadedOrder | null>(null)
  // A draft a step of approval applies to is submitted, not sent (docs/plans/po-approvals.md).
  const approval = useApprovalContext()
  const needs = (o: Pick<PurchaseOrderRow, 'status' | 'total_paisa'>) =>
    o.status === 'draft' && needsApproval(approval.rules, o.total_paisa, approval.featureOn)
  const withOrder = async (id: string, act: (o: LoadedOrder) => Promise<void> | void) => {
    setBusy(id)
    try {
      const loaded = await loadOrder(repo, id)
      if (loaded) await act(loaded)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(null)
    }
  }
  return {
    busy,
    receive: (id: string) =>
      withOrder(id, (o) => {
        const due = dueLines(o)
        if (due.length === 0) toast.info('Everything on this order has arrived.')
        else setReceiving({ order: o.order, due })
      }),
    /** A draft becomes ready to send (numbered), or is submitted when a step applies. */
    generate: (id: string) =>
      withOrder(id, async (o) => {
        if (!o.order.supplier_id) return toast.error('Edit the order and choose a supplier first.')
        if (!settings || !current) return
        const submit = needs(o.order)
        const numbering = await numberingFor(repo, settings, 'purchase_order', todayIso())
        await repo.commit(
          setPurchaseOrderStatus(
            { tenantId: current.tenant_id, now: new Date().toISOString() },
            purchaseOrderRow(o.order),
            submit ? 'submitted' : 'issued',
            { numbering },
          ).ops,
        )
        toast.success(submit ? 'Submitted for approval' : 'Order generated, ready to send')
      }),
    /** A ready or approved order: print, PDF or share it, and mark it sent. */
    send: (id: string) => withOrder(id, (o) => setSending(o)),
    /** Whether this draft is submitted for approval rather than generated. */
    needs,
    /** A row's order for its print menu. */
    loadPrint: async (id: string): Promise<DocToPrint> => {
      const loaded = await loadOrder(repo, id)
      if (!loaded) throw new Error('This order is no longer here.')
      return purchasePrint(loaded, lookups, settings)
    },
    dialog: (
      <>
        {receiving && <ReceiveStockDialog order={receiving} onClose={() => setReceiving(null)} />}
        {sending && <SendOrderDialog order={sending} onClose={() => setSending(null)} />}
      </>
    ),
  }
}

const ROW_BUTTON = 'h-[34px] text-[13px]'
const ROW_ICON =
  'size-[34px] border border-border bg-surface text-ink max-sm:size-10! max-sm:flex-none!'
/** The row the arrow keys are on. */
const ROW_ACTIVE = 'sm:bg-buy-wash sm:shadow-[inset_3px_0_0_var(--color-buy)]'

function OrdersTable({
  rows,
  supplierName,
  actions,
  activeId,
}: {
  rows: PurchaseOrderBoardRow[]
  /** Left out on a supplier's own page. */
  supplierName?: (id: string | null) => string | null
  actions: ReturnType<typeof useOrderActions>
  activeId?: string | null
}) {
  const settings = useSettings()
  const openItem = useOpen()
  const canEdit = useCan('staff')
  const today = todayIso()
  return (
    <Table stack="cards">
      <thead>
        <tr>
          <TH>Order</TH>
          <TH>Date</TH>
          <TH>What has arrived</TH>
          <TH>Status</TH>
          <TH align="right">Total</TH>
          <TH />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const o = row.order
          const state = purchaseBoardState(row)
          const stands = purchaseOrderStanding(row, today, (d) => dateShort(d, settings), qty)
          const name = o.number ?? (o.status === 'draft' ? 'Draft' : 'Not numbered yet')
          const open = state.label === 'Sent' || state.label === 'Part received'
          return (
            <TR
              key={o.id}
              aria-current={activeId === o.id ? 'true' : undefined}
              onClick={() => openItem(`/purchase-orders/${o.id}`)}
              className={cx(
                o.status === 'cancelled' && CANCELLED_ROW,
                activeId === o.id && ROW_ACTIVE,
              )}
            >
              <TD className="stack-title">
                <RowTitle
                  tile={
                    <IconTile area="buy" size="sm">
                      <ClipboardList className="size-4" />
                    </IconTile>
                  }
                  title={
                    <Link
                      to={`/purchase-orders/${o.id}`}
                      className="hover:underline focus-visible:underline"
                      onClick={(e) => {
                        e.stopPropagation()
                        moveClick(`/purchase-orders/${o.id}`, 'open')(e)
                      }}
                    >
                      {name}
                    </Link>
                  }
                  sub={
                    supplierName
                      ? (row.supplierName ?? supplierName(o.supplier_id) ?? 'No supplier yet')
                      : dateShort(o.order_date, settings)
                  }
                />
              </TD>
              <TD
                className={cx('whitespace-nowrap text-muted', 'max-sm:hidden')}
                title={dateLabel(o.order_date, settings)}
              >
                {dateShort(o.order_date, settings)}
              </TD>
              <TD className="stack-r3">
                <RowNote {...stands} />
              </TD>
              <TD className="stack-end stack-r2">
                <StateSwap state={state.label}>
                  <Badge tone={state.tone}>{state.label}</Badge>
                </StateSwap>
              </TD>
              <TD align="right" className="stack-end stack-r1 whitespace-nowrap font-bold">
                {money(o.total_paisa, settings)}
              </TD>
              <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                <RowActions>
                  {o.status === 'draft' && canEdit ? (
                    <Link
                      to={`/purchase-orders/${o.id}/edit`}
                      aria-label="Edit"
                      title="Edit"
                      className={cx(
                        'inline-flex items-center justify-center rounded-xl hover:bg-soft',
                        ROW_ICON,
                      )}
                    >
                      <Pencil className="size-4" aria-hidden />
                    </Link>
                  ) : (
                    o.status !== 'draft' && (
                      <DocPrintMenu
                        id={o.id}
                        load={() => actions.loadPrint(o.id)}
                        label={`Print ${name}`}
                        triggerClassName={ROW_ICON}
                      />
                    )
                  )}
                  {open && canEdit && (
                    <Button
                      size="sm"
                      variant="soft"
                      className={ROW_BUTTON}
                      aria-label={`Receive ${name}`}
                      loading={actions.busy === o.id}
                      onClick={() => void actions.receive(o.id)}
                    >
                      Receive
                    </Button>
                  )}
                  {o.status === 'draft' && canEdit && (
                    <Button
                      size="sm"
                      variant="soft"
                      className={ROW_BUTTON}
                      aria-label={`${actions.needs(o) ? 'Submit' : 'Generate'} ${name}`}
                      loading={actions.busy === o.id}
                      onClick={() => void actions.generate(o.id)}
                    >
                      {actions.needs(o) ? 'Submit' : 'Generate'}
                    </Button>
                  )}
                  {readyToSend(o) && canEdit && (
                    <Button
                      size="sm"
                      variant="soft"
                      className={ROW_BUTTON}
                      icon={<Send className="size-4" />}
                      aria-label={`Send ${name}`}
                      loading={actions.busy === o.id}
                      onClick={() => void actions.send(o.id)}
                    >
                      Send
                    </Button>
                  )}
                </RowActions>
              </TD>
            </TR>
          )
        })}
      </tbody>
    </Table>
  )
}

export function PurchaseOrdersScreen() {
  const canEdit = useCan('staff')
  const settings = useSettings()
  const navigate = useNavigate()
  const suppliers = useSuppliers()
  const actions = useOrderActions()
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [active, setActive] = useState<string | null>(null)
  const today = todayIso()
  const counts = useLive(
    (repo) => repo.purchases.orderCounts?.(today) ?? Promise.resolve(null),
    [today],
    [...ORDER_TABLES],
  ).data
  // The split and the pills need the local database; without it every order is listed.
  // Undefined while the counts load: the list waits, so it never flashes every row.
  const split = counts !== null
  const askedShow = params.get('show')
  const show: OrderShow =
    askedShow === 'finished' || (askedShow === 'reorder' && split) ? askedShow : 'open'
  const asked = params.get('status') as OrderPill | null
  const pill = show === 'open' && ORDER_PILLS.some((p) => p.value === asked) ? asked : null
  const go = (next: { show?: OrderShow; pill?: OrderPill | null }) => {
    const s = next.show ?? show
    const p = next.pill === undefined ? (next.show ? null : pill) : next.pill
    setParams(
      { ...(s !== 'open' ? { show: s } : {}), ...(p ? { status: p } : {}) },
      { replace: true },
    )
    setOffset(0)
    setActive(null)
  }
  const page = useOrders({
    show: split && show !== 'reorder' ? show : undefined,
    state: pill ?? undefined,
    search,
    limit: PAGE,
    offset,
  })
  const rows = page.data?.rows ?? []
  // Ready counts generated and approved orders: both wait to be sent.
  const openCount = counts
    ? counts.draft + counts.waiting + counts.ready + counts.sent + counts.part
    : 0
  const filtered = !!search || !!pill

  const listRef = useRef<HTMLDivElement>(null)
  useListKeys({
    ids: rows.map((r) => r.order.id),
    active,
    setActive,
    page: { offset, limit: PAGE, total: page.data?.total ?? 0, setOffset },
    list: listRef,
    open: () => active && navigate(`/purchase-orders/${active}`),
    keys: {
      p: () => active && openPrintMenu(active),
      s: () => {
        const row = rows.find((r) => r.order.id === active)
        if (row && readyToSend(row.order) && canEdit) void actions.send(row.order.id)
      },
    },
    enabled: show !== 'reorder',
  })

  // What to order is a tab of the section (layout/sections.tsx), at ?show=reorder.
  const tabs = counts && (
    <Segmented
      label="Open or finished"
      className="max-sm:w-full"
      value={show}
      onChange={(s) => go({ show: s })}
      options={[
        { value: 'open', label: `Open ${openCount}` },
        { value: 'finished', label: `Finished ${counts.finished}` },
      ]}
    />
  )
  if (show === 'reorder') return <WhatToOrder />

  return (
    <div>
      <PageHeader
        title="Purchase orders"
        subtitle={
          counts && openCount > 0 ? (
            <>
              {/* Generated orders are on order too: "on their way" alone would say 0. */}
              <b className="font-semibold text-ink">
                {counts.sent + counts.part > 0
                  ? `${counts.sent + counts.part} on their way`
                  : `${openCount} open`}
              </b>
              {counts.duePaisa > 0 && (
                <>
                  {' · '}
                  <b className="font-semibold text-ink">{money(counts.duePaisa, settings)}</b> still
                  to arrive
                </>
              )}
              {counts.late > 0 && (
                <span className="font-semibold text-danger"> · {counts.late} late</span>
              )}
              {counts.ready > 0 && (
                <span className="font-semibold text-primary"> · {counts.ready} ready to send</span>
              )}
              {counts.waiting > 0 && (
                <span className="font-semibold text-warning">
                  {' '}
                  · {counts.waiting} waiting approval
                </span>
              )}
              {counts.draft > 0 && ` · ${counts.draft} draft${counts.draft === 1 ? '' : 's'}`}
            </>
          ) : (
            'What you have asked suppliers for. Stock comes in when you receive it.'
          )
        }
        actions={
          canEdit && (
            <Link to="/purchase-orders/new" className={buttonClass()}>
              <ClipboardList className="size-4" aria-hidden />
              New purchase order
            </Link>
          )
        }
      />
      <SectionTabs section="purchase" />
      <ListToolbar>
        {tabs}
        {counts && show === 'open' && (
          <FilterPills label="Status">
            <FilterPill selected={!pill} count={openCount} onSelect={() => go({ pill: null })}>
              All
            </FilterPill>
            {/* Waiting approval shows only where approval is in use. */}
            {ORDER_PILLS.filter(
              (p) => p.value !== 'waiting' || counts.waiting > 0 || pill === 'waiting',
            ).map((p) => (
              <FilterPill
                key={p.value}
                selected={pill === p.value}
                count={counts[p.value]}
                onSelect={() => go({ pill: p.value })}
              >
                {p.label}
              </FilterPill>
            ))}
          </FilterPills>
        )}
        <SearchInput
          listKeys
          className="max-sm:w-full sm:ml-auto sm:w-64"
          value={search}
          onChange={(v) => {
            setSearch(v)
            setOffset(0)
          }}
          placeholder="Number or supplier…"
        />
      </ListToolbar>
      <ListFrame ref={listRef}>
        {!page.data || counts === undefined ? (
          <SkeletonTable cols={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="size-6" />}
            title={
              filtered
                ? 'No purchase orders match'
                : show === 'finished'
                  ? 'No finished orders yet'
                  : counts && counts.finished > 0
                    ? 'Nothing on order'
                    : 'No purchase orders yet'
            }
            description={
              filtered
                ? 'Try another status or search.'
                : show === 'finished'
                  ? 'Orders that have fully arrived, or were closed or cancelled, are kept here.'
                  : 'Write down what you are ordering from a supplier, then receive it when it arrives.'
            }
          />
        ) : (
          <>
            <OrdersTable
              rows={rows}
              supplierName={suppliers.name}
              actions={actions}
              activeId={active}
            />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>
                {split && show === 'open'
                  ? 'Late first, then the soonest due, then drafts.'
                  : 'Newest first.'}{' '}
                A row opens the order.
              </span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open <Kbd>P</Kbd> print{' '}
                {counts && counts.ready > 0 && (
                  <>
                    <Kbd>S</Kbd> send
                  </>
                )}
              </span>
            </div>
            <Pagination offset={offset} limit={PAGE} total={page.data.total} onChange={setOffset} />
          </>
        )}
      </ListFrame>
      {actions.dialog}
    </div>
  )
}

interface EditLine {
  key: string
  product: ProductRow
  qtyMilli: number | null
  costPaisa: number | null
  /** The purchase request line it was made from: kept when a draft is edited. */
  requestLineId?: string | null
}

/**
 * Writing a purchase order. `edit` changes a draft in place; `revise` starts
 * from a sent order and saves a new one, closing the old one in the same tx.
 */
export function PurchaseOrderEditorScreen({ mode }: { mode: 'new' | 'edit' | 'revise' }) {
  const { id } = useParams()
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const navigate = useNavigate()
  // A supplier's page starts an order for that supplier.
  const [query] = useSearchParams()
  const [supplierId, setSupplierId] = useState(query.get('supplier') ?? '')
  const [locationId, setLocationId] = useState<string | null>(null)
  const [expected, setExpected] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<EditLine[]>([])
  const [busy, setBusy] = useState(false)
  const location = locationId ?? lookups.activeLocations[0]?.id ?? null

  // A new order keeps what was typed on this device until it is saved
  // (docs/plans/dialog-safety.md); edits and revisions of a stored order do not.
  const keyOfDraft = mode === 'new' && current ? draftKey(current.tenant_id, 'po', 'new') : null
  const packed: PoDraft = {
    supplierId,
    locationId,
    expected,
    notes,
    lines: lines.map((l) => ({
      productId: l.product.id,
      qtyMilli: l.qtyMilli,
      costPaisa: l.costPaisa,
    })),
  }
  const packedText = JSON.stringify(packed)
  const [startText] = useState(packedText)
  const touched = packedText !== startText && poDraftHasWork(packed)
  const [offer, setOffer] = useState(() =>
    keyOfDraft ? readDraft(keyOfDraft, parsePoDraft) : null,
  )
  const [dropped, setDropped] = useState(0)
  useEffect(() => {
    if (!keyOfDraft || offer) return
    const timer = window.setTimeout(() => {
      if (touched) saveDraft(keyOfDraft, JSON.parse(packedText) as PoDraft)
      else clearDraft(keyOfDraft)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [keyOfDraft, offer, touched, packedText])
  const continueDraft = async () => {
    if (!offer) return
    const found = new Map<string, ProductRow>()
    for (const pid of new Set(offer.value.lines.map((l) => l.productId))) {
      const p = await repo.get('products', pid)
      if (p && p.is_active && !p.deleted_at) found.set(pid, p)
    }
    const kept = offer.value.lines.flatMap((l): EditLine[] => {
      const product = found.get(l.productId)
      return product
        ? [{ key: crypto.randomUUID(), product, qtyMilli: l.qtyMilli, costPaisa: l.costPaisa }]
        : []
    })
    if (offer.value.supplierId) setSupplierId(offer.value.supplierId)
    setLocationId(offer.value.locationId)
    setExpected(offer.value.expected)
    setNotes(offer.value.notes)
    setLines(kept)
    setDropped(offer.value.lines.length - kept.length)
    setOffer(null)
  }
  const startNew = () => {
    if (keyOfDraft) clearDraft(keyOfDraft)
    setOffer(null)
  }

  // The stored order fills the form once.
  const source = useLive(
    async (r) => {
      if (mode === 'new' || !id) return null
      const detail = await r.purchases.orderDetail(id)
      if (!detail) return null
      const products = await Promise.all(detail.lines.map((l) => r.get('products', l.product_id)))
      return { ...detail, products }
    },
    [mode, id],
    ['purchase_orders', 'purchase_order_lines'],
  ).data
  const loaded = useRef<string | null>(null)
  useEffect(() => {
    if (!source || loaded.current === source.order.id) return
    loaded.current = source.order.id
    const { order } = source
    const stored = source.lines.flatMap((l, i): EditLine[] => {
      const product = source.products[i]
      return product
        ? [
            {
              key: l.id,
              product,
              qtyMilli: l.qty_ordered_milli,
              costPaisa: l.unit_cost_paisa,
              // A revision is a new order: the old one (closed) keeps what it ordered.
              requestLineId: mode === 'edit' ? l.purchase_request_line_id : null,
            },
          ]
        : []
    })
    // Async on purpose: an effect must not set state while it runs.
    void Promise.resolve().then(() => {
      setSupplierId(order.supplier_id ?? '')
      setLocationId(order.location_id)
      setExpected(order.expected_date ?? '')
      setNotes(order.notes ?? '')
      setLines(stored)
    })
  }, [source, mode])

  // A carton's barcode brings its quantity (docs/plans/free-goods.md, part 3).
  const add = (product: ProductRow, pack?: ProductCodeRow | null) =>
    setLines((ls) =>
      ls.some((l) => l.product.id === product.id)
        ? pack
          ? ls.map((l) =>
              l.product.id === product.id
                ? { ...l, qtyMilli: (l.qtyMilli ?? 0) + pack.qty_milli }
                : l,
            )
          : ls
        : [
            ...ls,
            {
              key: crypto.randomUUID(),
              product,
              qtyMilli: pack?.qty_milli ?? null,
              costPaisa: product.cost_price_paisa,
            },
          ],
    )
  const update = (key: string, patch: Partial<EditLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  const total = lines.reduce(
    (sum, l) =>
      sum + purchaseLineCost({ qtyMilli: l.qtyMilli ?? 0, unitCostPaisa: l.costPaisa ?? 0 }),
    0,
  )
  const incomplete = lines.length === 0 || lines.some((l) => !l.qtyMilli)
  // A step of approval applies to this total: the order is submitted, not sent.
  const approval = useApprovalContext()
  const submit = needsApproval(approval.rules, total, approval.featureOn)

  // The main button keeps this device's last choice: generate only, or generate and send.
  const [choice, setChoice] = useState<GenerateChoice>(readGenerateChoice)

  /** Saves a draft, or generates the order (ready to send) and, with `andSend`, opens Send. */
  const save = async (asDraft: boolean, andSend = false) => {
    if (!settings || !current || incomplete) return
    if (!asDraft && !supplierId) {
      toast.error('Choose the supplier the order goes to.')
      return
    }
    setBusy(true)
    try {
      const today = todayIso()
      const ctx = { tenantId: current.tenant_id, now: new Date().toISOString() }
      // The stored order as it is right now: another device may have sent or closed it.
      const stored = mode === 'new' || !id ? null : await repo.purchases.orderDetail(id)
      if (mode !== 'new' && !stored) {
        toast.error('This purchase order no longer exists.')
        return
      }
      if (mode === 'edit' && stored?.order.status !== 'draft') {
        toast.error('This purchase order has been sent. Revise it instead.')
        return
      }
      const built = buildPurchaseOrder(
        ctx,
        {
          id: mode === 'edit' ? stored?.order.id : undefined,
          status: asDraft ? 'draft' : submit ? 'submitted' : 'issued',
          supplierId: supplierId || null,
          locationId: location,
          orderDate: today,
          expectedDate: expected || null,
          notes,
          lines: lines.map((l) => ({
            productId: l.product.id,
            qtyMilli: l.qtyMilli!,
            unitCostPaisa: l.costPaisa ?? 0,
            requestLineId: l.requestLineId ?? null,
          })),
          numbering: asDraft
            ? undefined
            : await numberingFor(repo, settings, 'purchase_order', today),
        },
        mode === 'edit' ? (stored?.lines ?? []) : [],
      )
      const ops = [...built.ops]
      // A revision replaces the sent order: that one is closed, with the reason.
      if (mode === 'revise' && stored && canMovePurchaseOrder(stored.order, 'closed')) {
        ops.unshift(
          ...setPurchaseOrderStatus(ctx, purchaseOrderRow(stored.order), 'closed', {
            reason: 'Revised',
          }).ops,
        )
      }
      await repo.commit(ops)
      if (keyOfDraft) clearDraft(keyOfDraft)
      toast.success(
        asDraft
          ? 'Draft saved'
          : submit
            ? 'Submitted for approval'
            : 'Order generated, ready to send',
      )
      navigate(`/purchase-orders/${built.order.id}${andSend && !submit ? '?send=1' : ''}`)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const generate = (how: GenerateChoice) => {
    setChoice(how)
    saveGenerateChoice(how)
    void save(false, how === 'send')
  }

  // Ctrl/⌘ Enter generates, with Shift generates and sends; from inside the fields too.
  const onKey = useEffectEvent((e: KeyboardEvent) => {
    if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey) || busy || incomplete || offer) return
    e.preventDefault()
    if (submit) void save(false)
    else generate(e.shiftKey ? 'send' : 'generate')
  })
  useEffect(() => {
    const listen = (e: KeyboardEvent) => onKey(e)
    window.addEventListener('keydown', listen)
    return () => window.removeEventListener('keydown', listen)
  }, [])

  const back = mode === 'new' || !id ? '/purchase-orders' : `/purchase-orders/${id}`
  return (
    <div>
      <Link
        to={back}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> {mode === 'new' ? 'Purchase orders' : 'Back to the order'}
      </Link>
      <PageHeader
        title={
          mode === 'new'
            ? 'New purchase order'
            : mode === 'edit'
              ? 'Edit purchase order'
              : `Revise ${source?.order.number ?? 'purchase order'}`
        }
        subtitle={
          mode === 'revise'
            ? 'Saving makes a new order and closes the old one. Stock does not change.'
            : 'What you are asking a supplier for. Stock does not change until you receive it.'
        }
      />
      {offer && (
        <div className="mb-4">
          <DraftBanner
            savedAt={offer.savedAt}
            summary={`${offer.value.lines.length} line${offer.value.lines.length === 1 ? '' : 's'}`}
            onStartNew={startNew}
            onContinue={() => void continueDraft()}
          />
        </div>
      )}
      {dropped > 0 && (
        <div className="mb-4">
          <DroppedNote count={dropped} />
        </div>
      )}
      <div
        inert={!!offer}
        className={cx('grid items-start gap-4 lg:grid-cols-[1fr_360px]', offer && 'opacity-50')}
      >
        <Card className="p-0">
          <div className="border-b border-border p-4">
            <ProductPicker
              onPick={add}
              placeholder="Scan or search products to order…"
              addNew="product"
              autoFocus={finePointer}
            />
          </div>
          {lines.length === 0 ? (
            <EmptyState
              icon={<ClipboardList className="size-6" />}
              title="Nothing on the order yet"
              description="Scan a barcode, or type a product name or SKU and press Enter."
            />
          ) : (
            <ul className="divide-y divide-border">
              {lines.map((l) => (
                <li
                  key={l.key}
                  className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 p-4 sm:grid-cols-[1fr_130px_140px_110px_auto] sm:gap-3"
                >
                  <div className="col-span-3 min-w-0 sm:col-span-1">
                    <div className="font-medium">{l.product.name}</div>
                    <div className="text-xs text-muted">{l.product.sku}</div>
                  </div>
                  <QtyInput
                    aria-label={`Quantity of ${l.product.name}`}
                    placeholder="Quantity"
                    milli={l.qtyMilli}
                    allowsDecimal={lookups.unit(l.product.unit_id)?.allows_decimal ?? false}
                    onValue={(m) => update(l.key, { qtyMilli: m })}
                  />
                  <MoneyInput
                    aria-label={`Cost of ${l.product.name}`}
                    placeholder="Unit cost"
                    paisa={l.costPaisa}
                    onValue={(p) => update(l.key, { costPaisa: p })}
                  />
                  <div className="tabular text-right text-sm max-sm:hidden">
                    {money(
                      purchaseLineCost({
                        qtyMilli: l.qtyMilli ?? 0,
                        unitCostPaisa: l.costPaisa ?? 0,
                      }),
                      settings,
                    )}
                  </div>
                  <IconButton
                    label={`Remove ${l.product.name}`}
                    onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="space-y-4 lg:sticky lg:top-0">
          <Field label="Supplier">
            {(fieldId) => (
              <SupplierPicker id={fieldId} value={supplierId} onChange={setSupplierId} />
            )}
          </Field>
          {lookups.activeLocations.length > 1 && (
            <Field label="Deliver to">
              {(fieldId) => (
                <LocationSelect
                  id={fieldId}
                  locations={lookups.activeLocations}
                  value={location}
                  onChange={setLocationId}
                />
              )}
            </Field>
          )}
          {expected && settings ? (
            <DateInput
              label="Needed by"
              value={expected}
              onChange={setExpected}
              calendar={settings.date_display === 'ad' ? 'ad' : 'bs'}
            />
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setExpected(todayIso())}>
              Add a needed-by date
            </Button>
          )}
          <Field label="Note for the supplier">
            {(fieldId) => (
              <Textarea
                id={fieldId}
                rows={2}
                maxLength={500}
                placeholder="Optional, printed on the order"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-baseline justify-between border-t border-dashed border-border pt-3">
            <span className="font-semibold">Total</span>
            <span className="text-metric">{money(total, settings)}</span>
          </div>
          {submit ? (
            <Button
              size="lg"
              className="w-full"
              loading={busy}
              disabled={incomplete}
              onClick={() => void save(false)}
            >
              Submit for approval
            </Button>
          ) : (
            // Phones have the two buttons in the bar at the bottom instead.
            <SplitButton
              className="max-sm:hidden"
              label={choice === 'send' ? 'Generate and send' : 'Generate PO'}
              icon={
                choice === 'send' ? (
                  <Send className="size-4" />
                ) : (
                  <ClipboardList className="size-4" />
                )
              }
              loading={busy}
              disabled={incomplete}
              onClick={() => generate(choice)}
              menuLabel="More ways to generate"
            >
              <MenuItem
                checked={choice === 'generate'}
                icon={
                  choice === 'generate' ? <Check className="size-4" /> : <span className="size-4" />
                }
                hint={
                  <span className="flex gap-1">
                    <Kbd>Ctrl</Kbd>
                    <Kbd>Enter</Kbd>
                  </span>
                }
                onSelect={() => generate('generate')}
              >
                <span className="block font-semibold">Generate PO</span>
                <span className="block text-xs text-muted">
                  Numbered and kept ready. Send it later from the order.
                </span>
              </MenuItem>
              <MenuItem
                checked={choice === 'send'}
                icon={
                  choice === 'send' ? <Check className="size-4" /> : <span className="size-4" />
                }
                hint={
                  <span className="flex gap-1">
                    <Kbd>Ctrl</Kbd>
                    <Kbd>⇧</Kbd>
                    <Kbd>Enter</Kbd>
                  </span>
                }
                onSelect={() => generate('send')}
              >
                <span className="block font-semibold">Generate and send</span>
                <span className="block text-xs text-muted">
                  Numbered, then print, PDF or share it with the supplier.
                </span>
              </MenuItem>
              <p className="px-2.5 pt-1 pb-1.5 text-xs text-subtle">
                The button keeps your last choice on this device.
              </p>
            </SplitButton>
          )}
          {submit && (
            <p className="text-center text-xs text-muted">
              This total needs approval before it goes to the supplier.
            </p>
          )}
          {mode !== 'revise' && (
            <Button
              variant="secondary"
              className="w-full max-sm:border-0 max-sm:bg-transparent max-sm:text-primary"
              disabled={busy || incomplete}
              onClick={() => void save(true)}
            >
              Save as draft
            </Button>
          )}
        </Card>
      </div>
      {/* On a phone the two ways to generate wait at the bottom: a menu is small for a thumb. */}
      {!submit && (
        <div className="sticky bottom-24 z-10 mt-4 flex gap-2 rounded-card bg-surface p-2 shadow-card sm:hidden">
          <Button
            variant="secondary"
            className="flex-1 px-3 whitespace-nowrap"
            disabled={busy || incomplete}
            onClick={() => generate('send')}
          >
            Generate and send
          </Button>
          <Button
            className="flex-1 px-3 whitespace-nowrap"
            loading={busy}
            disabled={incomplete}
            onClick={() => generate('generate')}
          >
            Generate PO
          </Button>
        </div>
      )}
    </div>
  )
}

/** Where a generated order stands: generated, not sent yet, nothing received (po-ready.md). */
function ReadySteps({
  generated,
  number,
  received,
}: {
  generated: string
  number: string | null
  received: boolean
}) {
  const step = (icon: ReactNode, tone: string, title: string, who: string, note: string) => (
    <li className="flex gap-3 border-t border-border py-2.5 first:border-t-0">
      <span className={cx('grid size-8 shrink-0 place-items-center rounded-full', tone)}>
        {icon}
      </span>
      <span className="min-w-0 text-sm">
        <span className="block font-semibold">{title}</span>
        <span className="block text-muted">{who}</span>
        <span className="block text-xs font-medium">{note}</span>
      </span>
    </li>
  )
  return (
    <Card>
      <h3 className="mb-1 text-sm font-semibold">Where it stands</h3>
      <ol>
        {step(
          <ClipboardList className="size-4" />,
          'bg-success-light text-success',
          'Generated',
          generated,
          number ? `Numbered ${number}` : 'Numbered when it syncs',
        )}
        {step(
          <Send className="size-4" />,
          'bg-primary-light text-primary',
          'Sent',
          'Not yet',
          'Send it when you are ready',
        )}
        {step(
          <PackageCheck className="size-4" />,
          'bg-soft text-subtle',
          'Received',
          received ? 'Some has arrived' : 'Nothing yet',
          'Goods that come first can still be received; that marks it sent',
        )}
      </ol>
    </Card>
  )
}

export function PurchaseOrderDetailScreen() {
  const { id = '' } = useParams()
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const canEdit = useCan('staff')
  const canManage = useCan('manager')
  const toast = useToast()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [receiving, setReceiving] = useState(false)
  const [sending, setSending] = useState(false)
  // "Generate and send" in the editor lands here with ?send=1: Send opens at once.
  const [params, setParams] = useSearchParams()
  const [ask, setAsk] = useState<null | 'cancelled' | 'closed' | 'delete'>(null)
  const [reason, setReason] = useState('')
  const approval = useApprovalContext()
  const names = useStaffNames()
  const [printing, setPrinting] = useState<DocToPrint | null>(null)
  const detail = useLive(
    async (r) => {
      const d = await r.purchases.orderDetail(id)
      if (!d) return null
      const [products, supplier] = await Promise.all([
        Promise.all(d.lines.map((l) => r.get('products', l.product_id))),
        d.order.supplier_id ? r.get('suppliers', d.order.supplier_id) : null,
      ])
      return { ...d, products, supplier }
    },
    [id],
    [
      'purchase_orders',
      'purchase_order_lines',
      'goods_receipts',
      'goods_receipt_lines',
      'products',
      'suppliers',
    ],
  )

  // What is owed to the supplier now, for managers (as on the supplier's page).
  const supplierId = detail.data?.order.supplier_id ?? null
  const owed = useLive(
    async (r) =>
      supplierId && canManage && r.payables
        ? ((await r.payables.owed()).find((o) => o.supplierId === supplierId) ?? null)
        : null,
    [supplierId, canManage],
    [...PAYABLE_TABLES],
  ).data

  if (!detail.data)
    return detail.loading ? <SkeletonDetail /> : <EmptyState title="Purchase order not found" />
  const { order, lines, receivedByLine, receipts, products, supplier } = detail.data
  const state = purchaseOrderState(order, { lines, by: receivedByLine })
  const receivedAny = Object.values(receivedByLine).some((q) => q > 0)
  const can = (to: PurchaseOrderStatus) => canEdit && canMovePurchaseOrder(order, to)
  // Only a sent order needs a manager to cancel; one still waiting has not gone out.
  const canCancel = can('cancelled') && (order.status !== 'sent' || canManage)
  // A rejected order is final: its correction is a new order, which asks again.
  const canRevise =
    canEdit &&
    (((order.status === 'sent' || order.status === 'issued') && !receivedAny) ||
      order.status === 'rejected')
  const submit =
    order.status === 'draft' && needsApproval(approval.rules, order.total_paisa, approval.featureOn)
  const canGenerate = order.status === 'draft' && can('issued')
  const ready = readyToSend(order)
  // Closing is for a sent order whose rest is not coming; one not sent yet is cancelled.
  const canClose = can('closed') && order.status === 'sent'
  const hasSecondary = canGenerate || canRevise
  const hasMore = canClose || canCancel || (order.status === 'draft' && canEdit)
  const showSend = ready && canEdit && (sending || params.get('send') === '1')
  const closeSend = () => {
    setSending(false)
    if (params.has('send')) setParams({}, { replace: true })
  }
  const due = dueLines(detail.data)
  const ctx = () => ({ tenantId: current!.tenant_id, now: new Date().toISOString() })
  const unitOf = (i: number) => lookups.unit(products[i]?.unit_id)?.symbol ?? ''
  const today = todayIso()
  const orderedMilli = lines.reduce((sum, l) => sum + l.qty_ordered_milli, 0)
  const gotMilli = lines.reduce(
    (sum, l) => sum + Math.min(receivedByLine[l.id] ?? 0, l.qty_ordered_milli),
    0,
  )
  const lastReceipt = receipts.reduce<string | null>(
    (last, g) => (last && last > g.receipt.receipt_date ? last : g.receipt.receipt_date),
    null,
  )
  // Late only while something is still expected on it.
  const waiting =
    ['issued', 'sent', 'approved', 'submitted'].includes(order.status) && due.length > 0
  const needed = order.expected_date
    ? countdown(order.order_date, order.expected_date, today, 'due')
    : null

  const run = async (ops: () => Promise<SyncOp[]> | SyncOp[], success: string) => {
    setBusy(true)
    try {
      await repo.commit(await ops())
      toast.success(success)
      setAsk(null)
      setReason('')
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }
  const move = (to: PurchaseOrderStatus, success: string, why?: string) =>
    run(async () => {
      const numbering =
        order.status === 'draft' && settings
          ? await numberingFor(repo, settings, 'purchase_order', todayIso())
          : undefined
      return setPurchaseOrderStatus(ctx(), purchaseOrderRow(order), to, { reason: why, numbering })
        .ops
    }, success)

  return (
    <div>
      <Link
        to="/purchase-orders"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Purchase orders
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="whitespace-nowrap">
              {order.number ??
                (order.status === 'draft' ? 'Draft purchase order' : 'Order (number on sync)')}
            </span>
            <StateSwap state={state.label}>
              <Badge tone={state.tone}>{state.label}</Badge>
            </StateSwap>
          </span>
        }
        subtitle={[
          dateLabel(order.order_date, settings),
          supplier?.name ?? 'No supplier yet',
          order.expected_date && `needed by ${dateLabel(order.expected_date, settings)}`,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {/* A generated order has Send in its banner; an approved one has it here. */}
            {order.status === 'approved' && canEdit && (
              <Button icon={<Send className="size-4" />} onClick={() => setSending(true)}>
                Send
              </Button>
            )}
            {(order.status === 'sent' || order.status === 'issued') &&
              canEdit &&
              due.length > 0 && (
                <Button
                  variant={order.status === 'issued' ? 'secondary' : 'primary'}
                  icon={<PackagePlus className="size-4" />}
                  onClick={() => setReceiving(true)}
                >
                  Receive
                </Button>
              )}
            {order.status === 'draft' && canEdit && (
              <Link to={`/purchase-orders/${order.id}/edit`} className={buttonClass()}>
                <Pencil className="size-4" aria-hidden />
                Edit
              </Link>
            )}
            <Button
              variant="secondary"
              icon={<Printer className="size-4" />}
              onClick={() =>
                setPrinting(purchasePrint({ order, lines, products, supplier }, lookups, settings))
              }
            >
              Print or save
            </Button>
          </>
        }
        secondary={
          hasSecondary && (
            <>
              {canGenerate && (
                <Button
                  variant="secondary"
                  icon={submit ? <Send className="size-4" /> : <ClipboardList className="size-4" />}
                  loading={busy}
                  onClick={() => {
                    if (!order.supplier_id)
                      toast.error('Edit the order and choose a supplier first.')
                    else if (submit) void move('submitted', 'Submitted for approval')
                    else void move('issued', 'Order generated, ready to send')
                  }}
                >
                  {submit ? 'Submit for approval' : 'Generate PO'}
                </Button>
              )}
              {canRevise && (
                <Link
                  to={`/purchase-orders/${order.id}/revise`}
                  className={buttonClass({ variant: 'secondary' })}
                >
                  <FilePen className="size-4" aria-hidden />
                  Revise
                </Link>
              )}
            </>
          )
        }
        more={
          hasMore && (
            <>
              {canClose && (
                <Button
                  variant="ghost"
                  icon={<Check className="size-4" />}
                  onClick={() => setAsk('closed')}
                >
                  Close order
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="ghost"
                  className="text-danger hover:text-danger"
                  icon={<Ban className="size-4" />}
                  onClick={() => setAsk('cancelled')}
                >
                  Cancel order
                </Button>
              )}
              {order.status === 'draft' && canEdit && (
                <Button
                  variant="ghost"
                  className="text-danger hover:text-danger"
                  icon={<Trash2 className="size-4" />}
                  onClick={() => setAsk('delete')}
                >
                  Delete draft
                </Button>
              )}
            </>
          )
        }
      />

      {order.status === 'issued' && (
        <div className="mb-4 flex items-center gap-3 rounded-card bg-primary-light p-3 text-primary max-sm:flex-wrap">
          <Clock className="size-5 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              Numbered and ready. Not sent{supplier ? ` to ${supplier.name}` : ''} yet.
            </p>
            <p className="text-sm">
              It can no longer be edited; change it with Revise. It counts as on order from now.
            </p>
          </div>
          {canEdit && (
            <Button
              icon={<Send className="size-4" />}
              className="max-sm:w-full"
              onClick={() => setSending(true)}
            >
              Send
            </Button>
          )}
        </div>
      )}
      {/* Four numbers first (board DocPO): two to a row on a phone. */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <BandStat
          label="Total"
          value={money(order.total_paisa, settings)}
          hint={`${lines.length} item${lines.length === 1 ? '' : 's'} · cost before VAT`}
        />
        <BandStat
          label="Received"
          value={`${qty(gotMilli)} of ${qty(orderedMilli)}`}
          meter={
            <Meter
              pct={orderedMilli ? (gotMilli / orderedMilli) * 100 : 0}
              tone={
                gotMilli >= orderedMilli && orderedMilli > 0
                  ? 'success'
                  : gotMilli
                    ? 'warning'
                    : 'primary'
              }
              label={`${qty(gotMilli)} of ${qty(orderedMilli)} received`}
            />
          }
          hint={
            <>
              <span
                className={cx(
                  'font-semibold',
                  gotMilli >= orderedMilli && orderedMilli > 0
                    ? 'text-success'
                    : gotMilli > 0 && 'text-warning',
                )}
              >
                {/* Not the badge's words: those say it once, at the top. */}
                {gotMilli === 0
                  ? 'Nothing yet'
                  : gotMilli >= orderedMilli
                    ? 'All here'
                    : `${qty(orderedMilli - gotMilli)} to come`}
              </span>
              {lastReceipt && ` · last on ${dateShort(lastReceipt, settings)}`}
            </>
          }
        />
        <BandStat
          label="Needed by"
          value={
            order.expected_date ? (
              <span title={dateLabel(order.expected_date, settings)}>
                {dateShort(order.expected_date, settings)}
              </span>
            ) : (
              'No date set'
            )
          }
          hint={
            needed && waiting ? (
              <span
                className={cx(
                  'font-semibold',
                  needed.tone === 'danger' && 'text-danger',
                  needed.tone === 'warning' && 'text-warning',
                )}
              >
                {needed.text}
              </span>
            ) : (
              `Ordered ${dateShort(order.order_date, settings)}`
            )
          }
        />
        <PartyCard
          label="Supplier"
          area="buy"
          name={supplier?.name ?? (order.supplier_id ? null : 'No supplier yet')}
          to={supplier ? `/suppliers/${supplier.id}` : null}
          lines={[
            [supplier?.phone, supplier?.pan_vat_number && `PAN ${supplier.pan_vat_number}`]
              .filter(Boolean)
              .join(' · '),
            owed && owed.owedPaisa > 0 && `You owe ${money(owed.owedPaisa, settings)}`,
          ]}
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="p-0">
          <Table stack>
            <thead>
              <tr>
                <TH>#</TH>
                <TH>Item</TH>
                <TH align="right">Ordered</TH>
                <TH>Received</TH>
                <TH align="right">Cost</TH>
                <TH align="right">Amount</TH>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const got = receivedByLine[l.id] ?? 0
                return (
                  <tr key={l.id}>
                    <TD className="text-muted max-sm:hidden">{l.line_no}</TD>
                    <TD className="stack-main stack-r1">
                      <Link
                        to={`/products/${l.product_id}`}
                        className="font-medium text-ink hover:text-primary hover:underline"
                      >
                        {products[i]?.name ?? 'Product'}
                      </Link>
                      <div className="text-xs text-muted">
                        {products[i]?.sku}
                        {/* Phones have no Received column. */}
                        <span className="sm:hidden">
                          {' '}
                          · {receivedWords(got, l.qty_ordered_milli)}
                        </span>
                      </div>
                    </TD>
                    <TD
                      align="right"
                      className="stack-start stack-r2 max-sm:text-left max-sm:text-muted max-sm:before:content-['Qty_']"
                    >
                      {qty(l.qty_ordered_milli)} {unitOf(i)}
                    </TD>
                    <TD className="w-44 max-sm:hidden">
                      <ReceivedBar got={got} ordered={l.qty_ordered_milli} />
                    </TD>
                    <TD
                      align="right"
                      className="stack-mid stack-r2 max-sm:text-muted max-sm:before:mr-0.5 max-sm:before:content-['@']"
                    >
                      {money(l.unit_cost_paisa, settings)}
                    </TD>
                    <TD align="right" className="stack-end stack-r1 max-sm:font-medium">
                      {money(
                        purchaseLineCost({
                          qtyMilli: l.qty_ordered_milli,
                          unitCostPaisa: l.unit_cost_paisa,
                        }),
                        settings,
                      )}
                    </TD>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </Card>

        <div className="space-y-4">
          <ApprovalCard doc={poDoc(order)} />
          {order.status === 'issued' && (
            <ReadySteps
              generated={`${(order.created_by && names[order.created_by]) || 'Someone'} · ${dateTime(order.updated_at, settings)}`}
              number={order.number}
              received={receivedAny}
            />
          )}
          <Card>
            <div className="flex justify-between text-base font-semibold">
              <span>Total</span>
              <span className="tabular">{money(order.total_paisa, settings)}</span>
            </div>
            <p className="mt-2 text-xs text-muted">{amountInWords(order.total_paisa)}</p>
            {lookups.activeLocations.length > 1 && order.location_id && (
              <p className="mt-2 text-sm text-muted">
                Deliver to {lookups.location(order.location_id)?.name}
              </p>
            )}
            {order.notes && (
              <p className="mt-3 rounded-xl bg-soft p-2 text-sm">
                <span className="text-muted">Note: </span>
                {order.notes}
              </p>
            )}
            {(order.status === 'cancelled' || order.status === 'closed') && (
              <p
                className={cx(
                  'mt-3 rounded-xl p-2 text-sm',
                  order.status === 'cancelled' ? 'bg-danger-light text-danger' : 'bg-soft',
                )}
              >
                {order.status === 'cancelled' ? 'Cancelled' : 'Closed'}: {order.close_reason}
              </p>
            )}
          </Card>
          {receipts.length > 0 && (
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Received so far</h3>
              <ReceiptLinks receipts={receipts} />
            </Card>
          )}
        </div>
      </div>

      {receiving && (
        <ReceiveStockDialog order={{ order, due }} onClose={() => setReceiving(false)} />
      )}
      {showSend && <SendOrderDialog order={detail.data} onClose={closeSend} />}
      <ConfirmDialog
        open={ask === 'cancelled' || ask === 'closed'}
        title={ask === 'closed' ? 'Close this order?' : 'Cancel this order?'}
        message={
          ask === 'closed'
            ? 'Nothing more is expected on it. What has been received stays in stock.'
            : 'It keeps its number and stays in the list as cancelled.'
        }
        confirmLabel={ask === 'closed' ? 'Close order' : 'Cancel order'}
        danger={ask === 'cancelled'}
        busy={busy}
        onClose={() => setAsk(null)}
        onConfirm={() => {
          if (!reason.trim()) {
            toast.error('Write a short reason first.')
            return
          }
          if (ask === 'cancelled' || ask === 'closed')
            void move(ask, ask === 'closed' ? 'Order closed' : 'Order cancelled', reason)
        }}
      >
        <Field label="Reason">
          {(fieldId) => (
            <Textarea
              id={fieldId}
              rows={2}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
      </ConfirmDialog>
      <ConfirmDialog
        open={ask === 'delete'}
        title="Delete this draft?"
        message="A draft has no number, so nothing is lost but what you typed."
        confirmLabel="Delete draft"
        danger
        busy={busy}
        onClose={() => setAsk(null)}
        onConfirm={() =>
          void run(
            () => [
              { table: 'purchase_orders', op: 'delete', row: { id: order.id } },
              ...lines.map((l): SyncOp => ({
                table: 'purchase_order_lines',
                op: 'delete',
                row: { id: l.id },
              })),
            ],
            'Draft deleted',
          ).then((done) => done && navigate('/purchase-orders'))
        }
      />
      {printing && <DocPrintDialog {...printing} onClose={() => setPrinting(null)} />}
    </div>
  )
}

/** "8 of 20 · 12 to come", or "All in", for one line of an order. */
function receivedWords(got: number, ordered: number): string {
  if (got >= ordered)
    return got > ordered ? `${qty(got)} of ${qty(ordered)} · more than ordered` : 'All in'
  return `${qty(got)} of ${qty(ordered)} · ${qty(ordered - got)} to come`
}

/** What has arrived on a line, with a bar (board DocPO). */
function ReceivedBar({ got, ordered }: { got: number; ordered: number }) {
  const all = got >= ordered
  return (
    <span className="block min-w-36">
      <span className="tabular flex justify-between gap-2 text-sm">
        <span>
          {qty(got)} of {qty(ordered)}
        </span>
        <span
          className={cx(
            'font-semibold',
            got > ordered
              ? 'text-warning'
              : all
                ? 'text-success'
                : got
                  ? 'text-warning'
                  : 'text-muted',
          )}
        >
          {got > ordered ? 'Over' : all ? 'All in' : `${qty(ordered - got)} to come`}
        </span>
      </span>
      <Meter
        className="mt-1"
        pct={ordered ? (got / ordered) * 100 : 0}
        tone={all ? 'success' : got ? 'warning' : 'primary'}
        label={`${qty(got)} of ${qty(ordered)} received`}
      />
    </span>
  )
}

// -----------------------------------------------------------------------------
// Purchases (goods receipts)
// -----------------------------------------------------------------------------

function ReceiptLinks({ receipts }: { receipts: GoodsReceiptSummary[] }) {
  const settings = useSettings()
  return (
    <ul className="space-y-1.5 text-sm">
      {receipts.map(({ receipt, totalPaisa }) => (
        <li key={receipt.id} className="flex items-center justify-between gap-3">
          <Link
            to={`/purchases/${receipt.id}`}
            className="min-w-0 truncate font-medium hover:text-primary hover:underline"
            title={dateLabel(receipt.receipt_date, settings)}
          >
            {dateShort(receipt.receipt_date, settings)}
            {receipt.reference && <span className="text-muted"> · {receipt.reference}</span>}
          </Link>
          <span className="tabular shrink-0">{money(totalPaisa, settings)}</span>
        </li>
      ))}
    </ul>
  )
}

function ReceiptsTable({
  rows,
  supplierName,
  onPay,
  bills: given,
  activeId,
  arrived,
}: {
  rows: GoodsReceiptSummary[]
  supplierName?: (id: string | null) => string | null
  /** Pay the bill of a delivery (shows a Pay button on rows that owe something). */
  onPay?: (bill: SupplierBillSummary) => void
  /** The rows' bills, when the screen has read them already (for its keys). */
  bills?: ReadonlyMap<string, SupplierBillSummary>
  activeId?: string | null
  arrived?: ReadonlySet<string>
}) {
  const settings = useSettings()
  const openItem = useOpen()
  const today = todayIso()
  const own = useReceiptBills(given ? [] : rows.map((r) => r.receipt.id))
  const bills = given ?? own
  return (
    <Table stack="cards">
      <thead>
        <tr>
          <TH>Purchase</TH>
          <TH>Received</TH>
          <TH>Where it stands</TH>
          <TH>Status</TH>
          <TH align="right">Bill total</TH>
          {onPay && <TH />}
        </tr>
      </thead>
      <tbody>
        {rows.map(({ receipt: g, totalPaisa, lineCount }) => {
          const bill = bills.get(g.id)
          const status = bill ? billStatus(bill, today) : null
          const late = !!status && status.lateDays > 0
          const supplier = supplierName?.(g.supplier_id)
          const items = `${lineCount} item${lineCount === 1 ? '' : 's'}`
          const received = dateShort(g.receipt_date, settings)
          const due = bill?.bill.due_date
            ? dueWords(bill.bill.due_date, today, (d) => dateShort(d, settings)).text
            : 'No due date'
          return (
            <TR
              key={g.id}
              onClick={() => openItem(`/purchases/${g.id}`)}
              aria-current={activeId === g.id ? 'true' : undefined}
              className={cx(arrived?.has(g.id) && arrivalClass, activeId === g.id && ROW_ACTIVE)}
            >
              <TD className="stack-title">
                <RowTitle
                  tile={
                    <IconTile area="buy" size="sm">
                      <PackageCheck className="size-4" />
                    </IconTile>
                  }
                  title={
                    <Link
                      to={`/purchases/${g.id}`}
                      className="hover:underline focus-visible:underline"
                      onClick={(e) => {
                        e.stopPropagation()
                        moveClick(`/purchases/${g.id}`, 'open')(e)
                      }}
                    >
                      {g.reference ?? `Received ${received}`}
                    </Link>
                  }
                  sub={
                    !supplierName ? (
                      items
                    ) : supplier && g.supplier_id ? (
                      <Link
                        to={`/suppliers/${g.supplier_id}`}
                        className="font-semibold text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {supplier}
                      </Link>
                    ) : (
                      'No supplier'
                    )
                  }
                />
              </TD>
              <TD
                className="whitespace-nowrap text-muted max-sm:hidden"
                title={dateLabel(g.receipt_date, settings)}
              >
                {received}
              </TD>
              <TD className="stack-r3">
                {!bill || !status ? (
                  <RowNote main={items} />
                ) : status.owedPaisa > 0 ? (
                  <RowNote
                    main={`${money(status.owedPaisa, settings)} owed`}
                    sub={due}
                    tone={late ? 'danger' : undefined}
                    toneOn="sub"
                  />
                ) : (
                  <RowNote main="Paid" sub={items} tone="success" />
                )}
              </TD>
              <TD className="stack-end stack-r2">
                {status ? (
                  <Badge tone={status.tone}>{status.label}</Badge>
                ) : (
                  <span className="text-xs text-subtle">No bill</span>
                )}
              </TD>
              <TD align="right" className="stack-end stack-r1 whitespace-nowrap font-bold">
                {money(bill ? bill.bill.total_paisa : totalPaisa, settings)}
              </TD>
              {onPay && (
                <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                  <RowActions>
                    {bill && status && status.owedPaisa > 0 && (
                      <Button
                        size="sm"
                        variant="soft"
                        className={ROW_BUTTON}
                        aria-label={`Pay ${supplier ?? 'the supplier'} for ${g.reference ?? received}`}
                        onClick={() => onPay(bill)}
                      >
                        Pay
                      </Button>
                    )}
                  </RowActions>
                </TD>
              )}
            </TR>
          )
        })}
      </tbody>
    </Table>
  )
}

const RECEIPT_TABLES = [
  'goods_receipts',
  'goods_receipt_lines',
  'goods_receipt_corrections',
] as const
type BillFilter = 'unpaid' | 'late' | 'paid' | 'none'
const BILL_FILTERS: { value: BillFilter | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'late', label: 'Late' },
  { value: 'paid', label: 'Paid' },
  { value: 'none', label: 'No bill' },
]

/** Every delivery taken into stock, with or without a purchase order. */
export function PurchasesScreen() {
  const canEdit = useCan('staff')
  const suppliers = useSuppliers()
  const [search, setSearch] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [offset, setOffset] = useState(0)
  const [receiving, setReceiving] = useState(false)
  const [bill, setBill] = useState<BillFilter | null>(null)
  const [paying, setPaying] = useState<SupplierBillSummary | null>(null)
  const [active, setActive] = useState<string | null>(null)
  const settings = useSettings()
  const openItem = useOpen()
  const today = todayIso()
  const page = useLive(
    (repo) =>
      repo.purchases.receipts({
        search,
        supplierId: supplierId || undefined,
        bill: bill ?? undefined,
        today,
        limit: PAGE,
        offset,
      }),
    [search, supplierId, bill, today, offset],
    [...RECEIPT_TABLES, ...PAYABLE_TABLES],
  )
  // How many deliveries each pill holds.
  const counts = useLive(
    async (repo) => {
      const of = (b: BillFilter) =>
        repo.purchases.receipts({ bill: b, today, limit: 1 }).then((p) => p.total)
      const [unpaid, late, paid, none] = await Promise.all([
        of('unpaid'),
        of('late'),
        of('paid'),
        of('none'),
      ])
      return { unpaid, late, paid, none }
    },
    [today],
    [...RECEIPT_TABLES, ...PAYABLE_TABLES],
  ).data
  const owed = useLive(
    (repo) => repo.payables?.totals(today) ?? Promise.resolve(null),
    [today],
    PAYABLE_TABLES,
  ).data
  const filters = JSON.stringify([search, supplierId, bill, offset])
  // A delivery received on another device slides into the list it reaches.
  const arrived = useArrivals(
    page.data?.rows.map((r) => r.receipt.id) ?? [],
    page.data !== undefined && !page.loading,
    filters,
  )
  const rows = page.data?.rows ?? []
  const bills = useReceiptBills(rows.map((r) => r.receipt.id))
  // The bill of the selected row, when something is still owed on it.
  const owedBill = (id: string | null) => {
    const b = id ? bills.get(id) : undefined
    return b && billStatus(b, today).owedPaisa > 0 ? b : null
  }
  const listRef = useRef<HTMLDivElement>(null)
  useListKeys({
    ids: rows.map((r) => r.receipt.id),
    active,
    setActive,
    page: { offset, limit: PAGE, total: page.data?.total ?? 0, setOffset },
    list: listRef,
    open: () => active && openItem(`/purchases/${active}`),
    keys: {
      p: () => {
        const b = canEdit ? owedBill(active) : null
        if (b) setPaying(b)
      },
    },
  })
  return (
    <div>
      <PageHeader
        title="Bills and payments"
        subtitle={
          owed && owed.owedPaisa > 0 ? (
            <>
              <b className="font-semibold text-ink">{money(owed.owedPaisa, settings)} to pay</b>
              {owed.overduePaisa > 0 && (
                <span className="font-semibold text-danger">
                  {' '}
                  · {money(owed.overduePaisa, settings)} is late
                </span>
              )}{' '}
              · Stock you have received, newest first.
            </>
          ) : (
            'Stock you have received, newest first.'
          )
        }
        actions={
          canEdit && (
            <Button icon={<PackagePlus className="size-4" />} onClick={() => setReceiving(true)}>
              Receive stock
            </Button>
          )
        }
      />
      <SectionTabs section="bills" />
      <ListToolbar>
        <FilterPills label="Bills">
          {BILL_FILTERS.map((f) => (
            <FilterPill
              key={f.value ?? 'all'}
              selected={bill === f.value}
              count={f.value ? (counts?.[f.value] ?? 0) : 0}
              onSelect={() => {
                setBill(f.value)
                setOffset(0)
              }}
            >
              {f.label}
            </FilterPill>
          ))}
        </FilterPills>
        <div className="max-sm:w-full sm:ml-auto sm:w-48">
          <Select
            aria-label="Supplier"
            value={supplierId}
            onChange={(e) => {
              setSupplierId(e.target.value)
              setOffset(0)
            }}
          >
            <option value="">All suppliers</option>
            {suppliers.list.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>
        <SearchInput
          listKeys
          className="max-sm:w-full sm:w-64"
          value={search}
          onChange={(v) => {
            setSearch(v)
            setOffset(0)
          }}
          placeholder="Search supplier bill no.…"
        />
      </ListToolbar>
      <ListFrame ref={listRef}>
        {!page.data ? (
          <SkeletonTable cols={5} />
        ) : page.data.rows.length === 0 ? (
          <EmptyState
            icon={<PackageCheck className="size-6" />}
            title={search || supplierId || bill ? 'No purchases match' : 'No purchases yet'}
            description={
              search || supplierId || bill
                ? 'Try another supplier or search.'
                : 'Each time you receive stock it is listed here with what it cost.'
            }
          />
        ) : (
          <>
            <ReceiptsTable
              rows={page.data.rows}
              supplierName={suppliers.name}
              onPay={canEdit ? setPaying : undefined}
              bills={bills}
              activeId={active}
              arrived={arrived}
            />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>Newest first. A row opens the delivery.</span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open{' '}
                {canEdit && (
                  <>
                    <Kbd>P</Kbd> pay
                  </>
                )}
              </span>
            </div>
            <Pagination offset={offset} limit={PAGE} total={page.data.total} onChange={setOffset} />
          </>
        )}
      </ListFrame>
      {receiving && <ReceiveStockDialog onClose={() => setReceiving(false)} />}
      {paying && (
        <PaySupplierDialog
          supplierId={paying.bill.supplier_id}
          supplierName={paying.supplierName ?? 'the supplier'}
          billId={paying.bill.id}
          onClose={() => setPaying(null)}
        />
      )}
    </div>
  )
}

/** One delivery: what came, at what cost, and the order it came against. */
export function PurchaseDetailScreen() {
  const { id = '' } = useParams()
  const settings = useSettings()
  const lookups = useLookups()
  const canCorrect = useCan('manager')
  const staff = useStaffNames()
  const [fixingId, setFixingId] = useState<string | null>(null)
  const detail = useLive(
    async (r) => {
      const d = await r.purchases.receiptDetail(id)
      if (!d) return null
      const [products, batches, supplier, order, corrections] = await Promise.all([
        Promise.all(d.lines.map((l) => r.get('products', l.product_id))),
        Promise.all(d.lines.map((l) => (l.batch_id ? r.get('batches', l.batch_id) : null))),
        d.receipt.supplier_id ? r.get('suppliers', d.receipt.supplier_id) : null,
        d.receipt.purchase_order_id ? r.get('purchase_orders', d.receipt.purchase_order_id) : null,
        r.list('goods_receipt_corrections', {
          where: { goods_receipt_id: id },
          orderBy: { column: 'created_at' },
          limit: 1000,
        }),
      ])
      return { ...d, products, batches, supplier, order, corrections: corrections.rows }
    },
    [id],
    [...RECEIPT_TABLES, 'products', 'suppliers', 'batches'],
  )
  if (!detail.data)
    return detail.loading ? <SkeletonDetail /> : <EmptyState title="Purchase not found" />
  const { receipt, lines, products, batches, supplier, order, corrections } = detail.data
  const fixing = lines.find((l) => l.id === fixingId)
  const cost = storedLineAmount
  const linesTotal = lines.reduce((sum, l) => sum + cost(l), 0)
  const total = Math.max(0, linesTotal - receipt.discount_paisa)
  const anyFree = lines.some((l) => l.free_qty_milli > 0)
  return (
    <div>
      <Link
        to="/purchases"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Purchases
      </Link>
      <PageHeader
        title={`Received ${dateLabel(receipt.receipt_date, settings)}`}
        subtitle={[
          supplier ? `from ${supplier.name}` : 'No supplier',
          receipt.reference && `bill no. ${receipt.reference}`,
          `into ${lookups.location(receipt.location_id)?.name ?? 'stock'}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card className="p-0">
          <h2 className="border-b border-border px-4 py-3 font-semibold">What came in</h2>
          <Table stack>
            <thead>
              <tr>
                <TH>#</TH>
                <TH>Item</TH>
                <TH align="right">Qty</TH>
                {anyFree && <TH align="right">Free</TH>}
                <TH align="right">Cost</TH>
                <TH align="right">Amount</TH>
                {canCorrect && <TH />}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id}>
                  <TD className="text-muted max-sm:hidden">{l.line_no}</TD>
                  <TD className="stack-main stack-r1">
                    <Link
                      to={`/products/${l.product_id}`}
                      className="font-medium text-ink hover:text-primary hover:underline"
                    >
                      {products[i]?.name ?? 'Product'}
                    </Link>
                    {batches[i] && (
                      <div className="text-xs text-muted">Batch {batches[i].batch_no}</div>
                    )}
                    {l.free_charge_paisa > 0 && (
                      <div className="text-xs text-muted">
                        Charged on free:{' '}
                        {l.free_charge_bp !== null && `${percentText(l.free_charge_bp)}% = `}
                        {money(l.free_charge_paisa, settings)}
                      </div>
                    )}
                    {l.discount_paisa > 0 && (
                      <div className="text-xs text-muted">
                        Discount: {l.discount_bp !== null && `${percentText(l.discount_bp)}% = `}
                        {money(l.discount_paisa, settings)}
                      </div>
                    )}
                    {corrections
                      .filter((c) => c.goods_receipt_line_id === l.id)
                      .map((c) => (
                        <div key={c.id} className="text-xs text-muted">
                          Rate corrected from {money(c.old_rate_paisa, settings)} to{' '}
                          {money(c.new_rate_paisa, settings)}
                          {c.created_by &&
                            staff[c.created_by] &&
                            ` by ${staff[c.created_by]}`} on{' '}
                          {dateLabel(c.created_at.slice(0, 10), settings)}
                          {c.reason && `. “${c.reason}”`}
                        </div>
                      ))}
                  </TD>
                  <TD
                    align="right"
                    className="stack-start stack-r2 whitespace-nowrap max-sm:text-left max-sm:text-muted max-sm:before:content-['Qty_']"
                  >
                    {l.entered_unit && l.entered_qty_milli !== null
                      ? unitWords(l.entered_qty_milli, l.entered_unit)
                      : `${qty(l.qty_milli)} ${lookups.unit(products[i]?.unit_id)?.symbol ?? ''}`}
                    {l.free_qty_milli > 0 && (
                      <span className="font-semibold text-success sm:hidden">
                        {' '}
                        + {qty(l.free_qty_milli)} free
                      </span>
                    )}
                  </TD>
                  {anyFree && (
                    <TD align="right" className="font-semibold text-success max-sm:hidden">
                      {l.free_qty_milli > 0 ? qty(l.free_qty_milli) : ''}
                    </TD>
                  )}
                  <TD
                    align="right"
                    className="stack-mid stack-r2 whitespace-nowrap max-sm:text-muted max-sm:before:mr-0.5 max-sm:before:content-['@']"
                  >
                    {money(typedRatePaisa(l), settings)}
                  </TD>
                  <TD
                    align="right"
                    className="stack-end stack-r1 whitespace-nowrap max-sm:font-medium"
                  >
                    {money(cost(l), settings)}
                  </TD>
                  {canCorrect && (
                    <TD align="right" className="stack-actions">
                      {l.qty_milli > 0 && (
                        <button
                          type="button"
                          className="whitespace-nowrap text-sm font-semibold text-primary hover:underline"
                          aria-label={`Correct rate of ${products[i]?.name ?? 'this line'}`}
                          onClick={() => setFixingId(l.id)}
                        >
                          Correct rate
                        </button>
                      )}
                    </TD>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <div className="space-y-4">
          <ReceiptBillCard
            receipt={receipt}
            supplier={supplier ? { id: supplier.id, name: supplier.name } : null}
            totalPaisa={total}
            corrected={corrections.length > 0}
          />
          <Card>
            {receipt.discount_paisa > 0 && (
              <div className="mb-2 space-y-1 text-sm text-muted">
                <div className="flex justify-between">
                  <span>Lines</span>
                  <span className="tabular">{money(linesTotal, settings)}</span>
                </div>
                <div className="flex justify-between">
                  <span>
                    Discount on the bill
                    {receipt.discount_bp !== null && ` (${percentText(receipt.discount_bp)}%)`}
                  </span>
                  <span className="tabular">−{money(receipt.discount_paisa, settings)}</span>
                </div>
              </div>
            )}
            <div className="flex justify-between text-base font-semibold">
              <span>Total cost</span>
              <span className="tabular">{money(total, settings)}</span>
            </div>
            {order && (
              <p className="mt-3 text-sm">
                <span className="text-muted">Against </span>
                <Link
                  to={`/purchase-orders/${order.id}`}
                  className="font-medium hover:text-primary hover:underline"
                >
                  {order.number ?? 'a purchase order'}
                </Link>
              </p>
            )}
            {receipt.notes && (
              <p className="mt-3 rounded-xl bg-soft p-2 text-sm">
                <span className="text-muted">Note: </span>
                {receipt.notes}
              </p>
            )}
          </Card>
        </div>
      </div>
      {fixing && (
        <CorrectRateDialog
          receipt={receipt}
          lines={lines}
          line={fixing}
          productName={products[lines.indexOf(fixing)]?.name ?? 'Product'}
          savedCostPaisa={products[lines.indexOf(fixing)]?.cost_price_paisa ?? null}
          onClose={() => setFixingId(null)}
        />
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// A supplier's page
// -----------------------------------------------------------------------------

export function SupplierDetailScreen() {
  const { id = '' } = useParams()
  const settings = useSettings()
  const canEdit = useCan('staff')
  // What the shop was billed and paid: managers and up (the owner's choice, 2026-10-26).
  const canStatement = useCan('manager')
  const [editing, setEditing] = useState(false)
  const [stating, setStating] = useState(false)
  const ledger = useLive(
    async (r): Promise<SupplierLedger | null> => {
      if (!r.payables) return null
      const [bills, payments, credits] = await Promise.all([
        r.payables.bills({ supplierId: id }),
        r.payables.payments({ supplierId: id }),
        r.payables.credits({ supplierId: id }),
      ])
      return { bills, payments, credits }
    },
    [id],
    PAYABLE_TABLES,
  ).data
  const statementOn = canStatement && !!ledger
  const dialogs = useSupplierDialogs()
  const supplier = useLive((r) => r.get('suppliers', id), [id], ['suppliers'])
  const orders = useOrders({ supplierId: id, limit: 20 })
  const orderActions = useOrderActions()
  const purchases = useLive(
    (repo) => repo.purchases.receipts({ supplierId: id, limit: 20 }),
    [id],
    [...RECEIPT_TABLES],
  ).data
  if (!supplier.data)
    return supplier.loading ? <SkeletonDetail /> : <EmptyState title="Supplier not found" />
  const s = supplier.data
  const open = orders.data?.rows.filter((o) =>
    ['Sent', 'Part received'].includes(purchaseBoardState(o).label),
  ).length
  return (
    <div>
      <Link
        to="/suppliers"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Suppliers
      </Link>
      <PageHeader
        title={s.name}
        subtitle={
          [s.contact_name, s.phone, s.email, s.pan_vat_number && `PAN ${s.pan_vat_number}`]
            .filter(Boolean)
            .join(' · ') || undefined
        }
        actions={canEdit && <SupplierPayButton dialogs={dialogs} />}
        secondary={
          canEdit && (
            <>
              {statementOn && (
                <Button
                  variant="secondary"
                  icon={<FilePdf className="size-4" />}
                  onClick={() => setStating(true)}
                >
                  Statement PDF
                </Button>
              )}
              <SupplierMoreButtons dialogs={dialogs} />
              <Link
                to={`/purchase-orders/new?supplier=${s.id}`}
                className={buttonClass({ variant: 'secondary' })}
              >
                <ClipboardList className="size-4" aria-hidden />
                New order
              </Link>
              <Button
                variant="secondary"
                icon={<Pencil className="size-4" />}
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
            </>
          )
        }
      />
      <SupplierMoney
        supplierId={s.id}
        supplierName={s.name}
        dialogs={dialogs}
        stat={
          <Stat
            area="stock"
            label="Last received"
            value={
              purchases
                ? purchases.rows[0]
                  ? dateShort(purchases.rows[0].receipt.receipt_date, settings)
                  : '—'
                : undefined
            }
            hint={
              purchases &&
              `${purchases.total} purchase${purchases.total === 1 ? '' : 's'}${open ? ` · ${open} order${open === 1 ? '' : 's'} open` : ''}`
            }
            icon={<PackageCheck className="size-5" />}
          />
        }
        tabs={[
          ...(statementOn
            ? [
                {
                  id: 'statement',
                  label: 'Statement',
                  content: <SupplierStatementTable ledger={ledger} today={todayIso()} />,
                },
              ]
            : []),
          {
            id: 'purchases',
            label: 'Purchases',
            count: purchases?.total,
            content: !purchases ? (
              <SkeletonTable rows={3} />
            ) : purchases.rows.length === 0 ? (
              <EmptyState title="Nothing received from this supplier yet" />
            ) : (
              <ReceiptsTable rows={purchases.rows} />
            ),
          },
          {
            id: 'orders',
            label: 'Purchase orders',
            count: orders.data?.total,
            content: !orders.data ? (
              <SkeletonTable rows={3} />
            ) : orders.data.rows.length === 0 ? (
              <EmptyState title="No purchase orders yet" />
            ) : (
              <OrdersTable rows={orders.data.rows} actions={orderActions} />
            ),
          },
        ]}
      />
      {editing && <PartyForm kind="suppliers" party={s} onClose={() => setEditing(false)} />}
      {stating && ledger && (
        <SupplierStatementSheet
          supplier={s}
          ledger={ledger}
          today={todayIso()}
          onClose={() => setStating(false)}
        />
      )}
      {orderActions.dialog}
    </div>
  )
}
